import { toast } from "sonner";

/**
 * Extract a human-readable message from an axios-like error or an Error.
 *
 * FastAPI 422 responses have `detail` as an array of `{loc, msg, type, ...}`
 * objects. Default toast handlers stringify that to `[object Object]` —
 * useless to the operator. This collapses such arrays into «field — msg; …»
 * so the toast reads like "username — String should match pattern …".
 */
type DetailItem = { loc?: unknown[]; msg?: string; message?: string };

interface ErrorLike {
  response?: { data?: { detail?: string | DetailItem[] | { detail?: string } } };
  message?: string;
}

export function extractErrorMessage(err: unknown, fallback = "Ошибка"): string {
  const e = err as ErrorLike;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  // Часть ручек ФБС кладёт код ошибки рядом с текстом: `{detail: {code, detail}}`.
  if (detail && !Array.isArray(detail) && typeof detail === "object") {
    const inner = (detail as { detail?: unknown }).detail;
    if (typeof inner === "string" && inner) return inner;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc)
          ? d.loc.filter((x) => x !== "body").join(".")
          : "";
        const msg = d.msg ?? d.message ?? "";
        return field ? `${field} — ${msg}` : msg;
      })
      .filter(Boolean)
      .join("; ") || fallback;
  }
  return e?.message ?? fallback;
}

/**
 * Как extractErrorMessage, но для запросов с `responseType: "blob"` (скачивание
 * файла): при ошибке axios отдаёт тело как Blob, поэтому `detail` надо достать
 * из текста Blob. Не JSON / нет detail → fallback.
 */
export async function blobErrorMessage(err: unknown, fallback = "Ошибка"): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const j = JSON.parse(await data.text());
      if (typeof j?.detail === "string") return j.detail;
    } catch {
      /* тело не JSON — используем fallback */
    }
    return fallback;
  }
  return extractErrorMessage(err, fallback);
}

/**
 * Структурированная ошибка бэкенда: `{code, detail}` в теле `detail`.
 *
 * Скан-экраны показывают по коду свой заголовок («Это на другую паллету»,
 * «Уже погружено»), поэтому разбор нужен всем и должен быть один: копий этого
 * сужения типа в проекте уже было несколько, и при добавлении поля на бэкенде
 * половина экранов молча теряла подсказку кладовщику.
 */
export function errorDetail<T extends { code: string } = { code: string; detail: string }>(
  err: unknown,
): T | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof (detail as T).code === "string") {
    return detail as T;
  }
  return null;
}

/**
 * Заголовок отказа «штрихкод в 1С заведён у нескольких товаров».
 *
 * Бэк отвечает на такой скан 409 `{code: "barcode_ambiguous", detail}`
 * (`scanning.engine.BarcodeAmbiguousError`) в ЛЮБОМ разделе. Это не сбой связи
 * и не «товар не найден», а ошибка справочника 1С: текст называет оба товара,
 * а заголовок должен сказать, что повтор скана не поможет.
 */
export const BARCODE_AMBIGUOUS_TITLE = "Штрихкод у нескольких товаров в 1С";

/** Заголовок ошибки резолва скана: свой у `barcode_ambiguous`, иначе `fallback`. */
export function scanErrorTitle(err: unknown, fallback: string): string {
  return errorDetail(err)?.code === "barcode_ambiguous" ? BARCODE_AMBIGUOUS_TITLE : fallback;
}

/**
 * Обработчик `onError` для мутаций: `onError: toastError("Ошибка сохранения")`.
 *
 * Один и тот же `toast.error(extractErrorMessage(e, …))` был выписан руками
 * одиннадцать раз в двух админских экранах — и ровно там, где эти строки
 * писались вручную, вместо разбора уезжал голый `detail`: у FastAPI 422 это
 * МАССИВ объектов, то есть «[object Object]» вместо причины отказа.
 */
export function toastError(fallback = "Ошибка"): (err: unknown) => void {
  return (err: unknown) => toast.error(extractErrorMessage(err, fallback));
}
